/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Spin } from 'antd';
import { cabinetOrderTable } from '../../constants/cabinet';
import { useDispatch } from 'react-redux';
import { postApi } from '../../redux/rest/restSlice';
import { ORDER_CABINET } from '../../constants/actionTypes';
import { unwrapResult } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { generatePaginationObj, getColumnSortOrder, getNextSort, TABLE_PAGINATION } from '../../utilities/helper';
import DataTable from '../DataTable';

// Add the order id search to a filters object (removed when empty)
const withOrderId = (filters = {}, orderId = '') => {
    const { order_id, ...rest } = filters;
    const term = (orderId || '').trim();
    return term ? { ...rest, order_id: term } : rest;
};

const Orders = () => {
    const [loading, setLoading] = useState(true);
    const [orderData, setOrderData] = useState({
        data: [],
        cabinets: [],
        order_status: [],
        filters: {},
        sort: null,
        pagination: {
            offset: 0,
            limit: 10,
            total: 0,
            current: 1,
        }
    });

    const [tempFilter, setTempFilter] = useState({
        cabinet_identifier: [],
        order_status: [],
    });
    const [orderIdSearch, setOrderIdSearch] = useState('');
    const dispatch = useDispatch();

    const fetchOrders = (page=1, filters={}, sort=null, limit=get(orderData, 'pagination.limit')) => {
        setLoading(true);
        const pageObj = generatePaginationObj(page, limit);

        dispatch(postApi({
            endpoint: ORDER_CABINET,
            payload: {
                filters: {...filters},
                ...pageObj,
                ...(sort ? { sortBy: sort.sortBy, sortDir: sort.sortDir } : {}),
            }
        }))
            .then(unwrapResult)
            .then((result) => {
                setLoading(false);
                setOrderData({
                    data: get(result, 'data'),
                    cabinets: get(result, 'cabinets'),
                    order_status: get(result, 'order_status'),
                    filters,
                    sort,
                    pagination: {
                        ...pageObj,
                        total: get(result, 'total'),
                        current: page,
                    },
                });
            })
            .catch(() => {
                setLoading(false);
            })
    };

    useEffect(() => {
        fetchOrders();
    }, [])

    const onSearchOrderId = (value) => {
        fetchOrders(
            1,
            withOrderId(get(orderData, 'filters'), value),
            get(orderData, 'sort'),
        );
    };

    const clearSearch = () => {
        setOrderIdSearch('');
        onSearchOrderId('');
    };

    const onTableChange = (paginationObj, _filters, sorter, extra) => {
        if (get(extra, 'action') === 'sort') {
            // Only "Order Created" (created_at) is sortable
            fetchOrders(1, get(orderData, 'filters'), getNextSort(sorter, get(orderData, 'sort')));
            return;
        }

        // Page change, or page size change (back to page 1)
        const pageSize = get(paginationObj, 'pageSize');
        const sizeChanged = pageSize !== get(orderData, 'pagination.limit');

        fetchOrders(
            sizeChanged ? 1 : get(paginationObj, 'current'),
            get(orderData, 'filters'),
            get(orderData, 'sort'),
            pageSize,
        );
    };

    const rows = get(orderData, 'data');
    const firstLoad = loading && !(rows && rows.length);
    const appliedFilters = get(orderData, 'filters') || {};
    const hasFilters = ['cabinet_identifier', 'order_status'].some(
        (key) => (appliedFilters[key] || []).length
    );

    return (
        <Spin spinning={loading && !firstLoad}>
            <div className='list-toolbar'>
                <div className='list-toolbar__main'>
                    <Input.Search
                        className='list-toolbar__search'
                        placeholder='Search by Order ID'
                        allowClear
                        value={orderIdSearch}
                        onChange={(e) => setOrderIdSearch(e.target.value)}
                        onSearch={onSearchOrderId}
                    />

                    <Select
                        mode={'multiple'}
                        allowClear
                        placeholder={'Select Cabinet'}
                        style={{ width: 170 }}
                        options={get(orderData, 'cabinets')}
                        onChange={(value) => {
                            setTempFilter((prev) => {
                                return {
                                    ...prev,
                                    cabinet_identifier: [...value]
                                }
                            })
                        }}
                    />

                    <Select
                        mode={'multiple'}
                        allowClear
                        placeholder={'Select Status'}
                        style={{ width: 200 }}
                        options={get(orderData, 'order_status')}
                        onChange={(value) => {
                            setTempFilter((prev) => {
                                return {
                                    ...prev,
                                    order_status: [...value]
                                }
                            })
                        }}
                    />
                </div>

                <div className='list-toolbar__actions'>
                    <Button
                        type="primary"
                        className='default-primary-btn'
                        onClick={() => {
                            fetchOrders(
                                get(orderData, 'pagination.current'),
                                withOrderId({...tempFilter}, orderIdSearch),
                                get(orderData, 'sort'),
                            )
                        }}
                    >
                        Apply Filter
                    </Button>
                </div>
            </div>

            <DataTable
                skeleton={firstLoad}
                scrollX={1000}
                rowKey='id'
                className='mt-20'
                dataSource={rows}
                columns={cabinetOrderTable(
                    get(orderData, 'pagination.offset'),
                    getColumnSortOrder(get(orderData, 'sort'), 'created_at'),
                )}
                pagination={{
                    ...TABLE_PAGINATION,
                    pageSize: get(orderData, 'pagination.limit'),
                    total: get(orderData, 'pagination.total'),
                    current: get(orderData, 'pagination.current'),
                }}
                onChange={onTableChange}
                emptyProps={{
                    noun: 'orders',
                    search: appliedFilters.order_id,
                    filtered: hasFilters,
                    onClearSearch: clearSearch,
                }}
            />
        </Spin>
    );
};

export default Orders;
